/**
 * MobileBottomNav — fixed bottom navigation for primary social pages.
 * Shows on: /feed, /mine-ture, /profil, /dashboard, /klubber, /events
 * Hides on: /kort (map has its own MapBottomBar), landing, auth, fullscreen replay/record, admin, reels/watch
 *
 * The central + FAB is larger, elevated, and glowing green — opens a
 * creation menu using existing routes and actions.
 */
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Rss, Map, Plus, Route, User, X, PenLine, Video, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

// Routes where bottom nav should NOT appear
const EXCLUDED_PREFIXES = [
  '/kort',                                              // map has its own bottom bar + Start tur
  '/beskeder', '/mine-beskeder',                        // messaging is a full-screen takeover — the
                                                        // fixed bottom nav must NOT overlap the composer
  '/login', '/register', '/glemt-adgangskode', '/reset-password',
  '/record-replay', '/replay-lab',
  '/reels', '/watch',
  '/admin', '/superadmin',
  '/timeline-preview', '/page-turn-test', '/timeline-showcase', '/living-preview',
];

function shouldShow(pathname) {
  for (const prefix of EXCLUDED_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return false;
  }
  return true;
}

// The landing page now shows the full bottom nav (same as Feed) so the + menu
// is identical in position, layout, and UX. isFabOnly is kept as a function
// for future use but no longer activates on '/'.
function isFabOnly(_pathname) {
  return false;
}

// FAB creation items — Feed content creation only
const FAB_ITEMS = [
  { icon: PenLine, labelKey: 'nav.createPost', to: '/feed?compose=1', color: '#2DFF88', textColor: '#0a1410' },
  { icon: Video, labelKey: 'nav.uploadVideo', to: '/feed?compose=video', color: '#ef4444', textColor: '#fff' },
  { icon: Calendar, labelKey: 'nav.createEvent', to: '/opret-event', color: '#818cf8', textColor: '#fff' },
];

export default function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [fabOpen, setFabOpen] = useState(false);
  const [navState, setNavState] = useState('normal'); // 'normal' | 'compact' | 'hidden'
  const lastScrollY = useRef(0);
  const scrollTimer = useRef(null);

  // Reset nav visibility on every route change
  useEffect(() => {
    setNavState('normal');
    lastScrollY.current = 0;
    setFabOpen(false);
  }, [location.pathname]);

  // Scroll-hide / scroll-show — attached once, uses stable setState refs
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastScrollY.current;

      if (Math.abs(delta) > 3) {
        if (delta > 0) {
          // Scrolling down: compact first (at 30px), then fully hide (at 80px)
          if (y > 80) {
            setNavState('hidden');
            setFabOpen(false);
          } else if (y > 30) {
            setNavState('compact');
            setFabOpen(false);
          }
        } else if (delta < 0) {
          // Scrolling up → restore immediately
          setNavState('normal');
        }
        lastScrollY.current = y;
      }

      // Auto-reveal after 2 s of no scroll activity
      clearTimeout(scrollTimer.current);
      scrollTimer.current = setTimeout(() => setNavState('normal'), 2000);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // capture:true catches scroll on any overflow container (iOS Safari, feed lists, etc.)
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onScroll, { capture: true });
      clearTimeout(scrollTimer.current);
    };
  }, []); // setState functions are stable — no deps needed

  if (!shouldShow(location.pathname)) return null;

  const fabOnly = isFabOnly(location.pathname);
  const isActive = (path) => {
    if (path === '/feed') return location.pathname === '/feed';
    if (path === '/kort') return location.pathname === '/kort';
    if (path === '/mine-ture') return location.pathname === '/mine-ture';
    if (path === '/profil') {
      return location.pathname.startsWith('/profil') || location.pathname === '/dashboard';
    }
    return false;
  };

  const profilePath = user ? '/profil' : '/login';

  const handleFabItem = (item) => {
    setFabOpen(false);
    navigate(item.to);
  };

  const activeStyle = { color: '#2DFF88' };
  const inactiveStyle = { color: '#71717a' };

  return (
    <>
      {/* Backdrop */}
      {fabOpen && (
        <div
          className="fixed inset-0 z-[50] bg-black/70"
          style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
          onClick={() => setFabOpen(false)}
          data-testid="fab-backdrop"
        />
      )}

      {/* FAB creation menu — slides up above the + button */}
      {fabOpen && (
        <div
          className="fixed z-[52] flex flex-col items-end gap-3"
          style={{
            bottom: fabOnly
              ? 'calc(env(safe-area-inset-bottom, 0px) + 68px)'
              : 'calc(env(safe-area-inset-bottom, 0px) + 58px)',
            right: fabOnly ? 20 : '50%',
            transform: fabOnly ? 'none' : 'translateX(50%)',
          }}
          data-testid="fab-menu"
        >
          {[...FAB_ITEMS].reverse().map((item, idx) => (
            <button
              key={item.labelKey}
              onClick={() => handleFabItem(item)}
              className="flex items-center gap-3 group"
              style={{
                animation: `slideUpFade 0.2s ease-out ${idx * 45}ms both`,
              }}
              data-testid={`fab-item-${idx}`}
            >
              {/* Label */}
              <span
                className="text-white text-sm font-semibold px-3.5 py-1.5 rounded-full shadow-lg"
                style={{
                  background: 'rgba(12,22,17,0.92)',
                  border: '1px solid rgba(232,220,192,0.12)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  whiteSpace: 'nowrap',
                }}
              >
                {t(item.labelKey)}
              </span>
              {/* Icon circle */}
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-xl flex-shrink-0"
                style={{ background: item.color }}
              >
                <item.icon className="w-5 h-5" style={{ color: item.textColor }} />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* FAB-only mode (landing page): show just the floating + button, no nav bar */}
      {fabOnly ? (
        <div
          className="fixed z-[51]"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
            right: 20,
          }}
          data-testid="landing-fab-wrapper"
        >
          <button
            onClick={() => setFabOpen(v => !v)}
            className="flex items-center justify-center transition-all active:scale-90"
            style={{
              width: 58,
              height: 58,
              borderRadius: '50%',
              background: fabOpen
                ? 'linear-gradient(135deg, #1aff77 0%, #00bb55 100%)'
                : 'linear-gradient(135deg, #2DFF88 0%, #00cc66 100%)',
              boxShadow: fabOpen
                ? '0 0 0 4px rgba(45,255,136,0.45), 0 8px 28px rgba(45,255,136,0.5)'
                : '0 0 0 3px rgba(45,255,136,0.22), 0 6px 24px rgba(45,255,136,0.38), 0 2px 8px rgba(0,0,0,0.6)',
              transform: fabOpen ? 'rotate(45deg) scale(1.05)' : 'none',
              transition: 'all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
            data-testid="fab-btn"
            aria-label="Opret"
          >
            {fabOpen
              ? <X className="w-7 h-7" style={{ color: '#0a1410' }} />
              : <Plus className="w-7 h-7" style={{ color: '#0a1410' }} />
            }
          </button>
        </div>
      ) : (
        <nav
          className="fixed bottom-0 left-0 right-0 z-[51]"
          style={{
            background: 'rgba(10, 18, 14, 0.95)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            borderTop: '1px solid rgba(232,220,192,0.07)',
            boxShadow: '0 -2px 14px rgba(0,0,0,0.38)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            transform: navState === 'hidden' ? 'translateY(110%)' : 'translateY(0)',
            transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: navState === 'hidden' ? 'none' : 'auto',
            willChange: 'transform',
          }}
          data-testid="mobile-bottom-nav"
        >
        <div className="flex items-end justify-around transition-all duration-200" style={{ height: navState === 'compact' ? 36 : 46 }}>

          {/* Feed */}
          <Link
            to="/feed"
            className="flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors active:opacity-70"
            data-testid="nav-feed"
          >
            <Rss className="w-[18px] h-[18px]" style={isActive('/feed') ? activeStyle : inactiveStyle} />
            {navState === 'normal' && (
              <span className="text-[10px] font-semibold" style={isActive('/feed') ? activeStyle : inactiveStyle}>
                {t('nav.feed')}
              </span>
            )}
          </Link>

          {/* Kort */}
          <Link
            to="/kort"
            className="flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors active:opacity-70"
            data-testid="nav-kort"
          >
            <Map className="w-[18px] h-[18px]" style={isActive('/kort') ? activeStyle : inactiveStyle} />
            {navState === 'normal' && (
              <span className="text-[10px] font-semibold" style={isActive('/kort') ? activeStyle : inactiveStyle}>
                {t('nav.map')}
              </span>
            )}
          </Link>

          {/* FAB + (center, elevated) */}
          <div className="flex flex-col items-center justify-center flex-1" style={{ marginTop: navState === 'compact' ? -10 : -16 }}>
            <button
              onClick={() => setFabOpen(v => !v)}
              className="flex items-center justify-center transition-all active:scale-90"
              style={{
                width: 58,
                height: 58,
                borderRadius: '50%',
                background: fabOpen
                  ? 'linear-gradient(135deg, #1aff77 0%, #00bb55 100%)'
                  : 'linear-gradient(135deg, #2DFF88 0%, #00cc66 100%)',
                boxShadow: fabOpen
                  ? '0 0 0 4px rgba(45,255,136,0.45), 0 8px 28px rgba(45,255,136,0.5)'
                  : '0 0 0 3px rgba(45,255,136,0.22), 0 6px 24px rgba(45,255,136,0.38), 0 2px 8px rgba(0,0,0,0.6)',
                transform: fabOpen ? 'rotate(45deg) scale(1.05)' : 'none',
                transition: 'all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
              }}
              data-testid="fab-btn"
              aria-label={t('nav.create')}
            >
              {fabOpen
                ? <X className="w-7 h-7" style={{ color: '#0a1410' }} />
                : <Plus className="w-7 h-7" style={{ color: '#0a1410' }} />
              }
            </button>
          </div>

          {/* Rides */}
          <Link
            to="/mine-ture"
            className="flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors active:opacity-70"
            data-testid="nav-rides"
          >
            <Route className="w-[18px] h-[18px]" style={isActive('/mine-ture') ? activeStyle : inactiveStyle} />
            {navState === 'normal' && (
              <span className="text-[10px] font-semibold" style={isActive('/mine-ture') ? activeStyle : inactiveStyle}>
                {t('nav.rides')}
              </span>
            )}
          </Link>

          {/* Profil */}
          <Link
            to={profilePath}
            className="flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors active:opacity-70"
            data-testid="nav-profil"
          >
            <User className="w-[18px] h-[18px]" style={isActive('/profil') ? activeStyle : inactiveStyle} />
            {navState === 'normal' && (
              <span className="text-[10px] font-semibold" style={isActive('/profil') ? activeStyle : inactiveStyle}>
                {t('nav.profile')}
              </span>
            )}
          </Link>

        </div>
      </nav>
      )}

      {/* Keyframe for FAB item entrance */}
      <style>{`
        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(16px) scale(0.92); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
    </>
  );
}
